import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { notifyStore } from '~/stores'
import { SAFE_AREA } from '~/common/styles/responsive'

/*
 * Losing the connection is a state of the whole app, not the result of one
 * click, so it gets a banner that stays for as long as it is true — not a toast
 * per failed request. Before, a dropped connection produced no message at all:
 * the error link only reported GraphQL errors, and a network failure has none.
 *
 * Coming back online is worth one short confirmation, so the recruiter knows
 * they can carry on.
 */
// Centred by left/right + auto margins rather than left: 50% + translate: a
// translated box shrink-wraps to at most half the viewport, which on a phone
// turned the sentence into a tall, narrow column.
const Bar = styled.div`
	position: fixed;
	top: 0;
	left: 16px;
	right: 16px;
	width: fit-content;
	margin-left: auto;
	margin-right: auto;
	z-index: 10060;
	display: flex;
	align-items: center;
	gap: 10px;
	max-width: calc(100vw - 32px);
	margin-top: calc(10px + ${SAFE_AREA.top});
	padding: 10px 16px;
	border-radius: 10px;
	background: #1f2937;
	color: #fff;
	font-size: 14px;
	line-height: 1.4;
	font-weight: 500;
	box-shadow: 0 10px 24px rgba(17, 24, 39, 0.24);

	.dot {
		flex: none;
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: #f59e0b;
	}
`

const isOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false)

export const OFFLINE_MESSAGE = 'You’re offline. Changes you make now won’t be saved until you reconnect.'
export const BACK_ONLINE_MESSAGE = 'You’re back online.'

export const ConnectionBanner = () => {
	const [online, setOnline] = useState(isOnline)
	const wasOffline = useRef(!isOnline())

	useEffect(() => {
		const goOffline = () => {
			wasOffline.current = true
			setOnline(false)
		}
		const goOnline = () => {
			setOnline(true)
			if (wasOffline.current) notifyStore.success(BACK_ONLINE_MESSAGE)
			wasOffline.current = false
		}
		window.addEventListener('offline', goOffline)
		window.addEventListener('online', goOnline)
		return () => {
			window.removeEventListener('offline', goOffline)
			window.removeEventListener('online', goOnline)
		}
	}, [])

	if (online) return null

	return (
		<Bar role="status" aria-live="polite" data-testid="connection-banner">
			<span className="dot" aria-hidden="true" />
			{OFFLINE_MESSAGE}
		</Bar>
	)
}
